import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Briefcase, GraduationCap, Handshake, HeartHandshake, Plus, Scale, Send, Bot } from "lucide-react";
import { api } from "@/lib/api";
import { FadeUp, Overline, EASE } from "./Reveal";

const STEPS = [
  { num: "1", title: "Je crée mon profil", desc: "Je renseigne uniquement les informations nécessaires à la personnalisation de mon parcours." },
  { num: "2", title: "INTEGRA FRANCE comprend mon objectif", desc: "La plateforme me propose un parcours adapté à ma situation." },
  { num: "3", title: "J'avance à mon rythme", desc: "Je me forme, je révise, je pose mes questions et je suis mes prochaines étapes." },
];

export function HowItWorks() {
  return (
    <section id="comment" className="py-20 sm:py-28">
      <div className="container-integra">
        <FadeUp className="mx-auto max-w-2xl text-center">
          <Overline>Comment ça marche ?</Overline>
          <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Trois étapes pour démarrer sereinement.
          </h2>
        </FadeUp>

        <div className="relative mx-auto mt-14 grid max-w-5xl gap-10 md:grid-cols-3">
          <div className="absolute left-0 right-0 top-8 hidden h-px bg-gradient-to-r from-transparent via-bleu/25 to-transparent md:block" />
          {STEPS.map((s, i) => (
            <FadeUp key={s.num} delay={i * 0.15}>
              <div className="relative text-center" data-testid={`how-step-${s.num}`}>
                <div className="relative z-10 mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-bleu font-display text-2xl font-extrabold text-white shadow-xl shadow-bleu/30">
                  {s.num}
                </div>
                <h3 className="mt-6 font-display text-xl font-bold text-slate-900">{s.title}</h3>
                <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-slate-500">{s.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.3} className="mt-12 text-center">
          <Link to="/signup" className="btn-primary" data-testid="how-cta">
            Commencer mon parcours gratuitement <ArrowRight className="h-4 w-4" />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
}

export function QuestionCTA() {
  const [value, setValue] = useState("");
  const navigate = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    navigate("/signup");
  };
  return (
    <section id="question" className="relative overflow-hidden bg-bleu py-20 sm:py-28">
      <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-24 h-96 w-96 rounded-full bg-bleu-accent/30 blur-3xl" />
      <div className="container-integra relative">
        <div className="grid items-center gap-12 lg:grid-cols-[1fr_auto]">
          <FadeUp>
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-white/15 backdrop-blur">
              <Bot className="h-9 w-9 text-white" />
            </span>
            <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Vous ne savez pas par où commencer ?
            </h2>
            <p className="mt-4 max-w-xl text-lg text-white/80">
              Commencez simplement par nous expliquer votre situation.
            </p>

            <form onSubmit={submit} className="mt-8 max-w-xl" data-testid="question-form">
              <div className="flex items-center gap-2 rounded-full bg-white p-2 shadow-2xl shadow-bleu-night/40">
                <input
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="Ex. « Je vis en France depuis deux ans et je veux comprendre les prochaines étapes de mon parcours… »"
                  className="min-w-0 flex-1 rounded-full bg-transparent px-4 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
                  data-testid="search-callout-input"
                />
                <button
                  type="submit"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-rouge text-white transition-transform duration-300 hover:scale-105"
                  aria-label="Envoyer ma situation"
                  data-testid="search-callout-submit"
                >
                  <Send className="h-4.5 w-4.5" />
                </button>
              </div>
            </form>
          </FadeUp>

          <FadeUp delay={0.15} className="lg:text-right">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-bold text-bleu shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
              data-testid="question-cta"
            >
              Commencer mon parcours <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

const FAQ_ITEMS = [
  {
    q: "INTEGRA FRANCE est-il un service officiel de l'administration française ?",
    a: "Non. INTEGRA FRANCE est une plateforme indépendante destinée à faciliter l'accès à l'information et l'accompagnement. Les utilisateurs doivent toujours vérifier les informations administratives auprès des sources officielles compétentes.",
  },
  {
    q: "Dois-je savoir utiliser l'intelligence artificielle ?",
    a: "Non. Vous posez simplement vos questions avec vos propres mots, comme vous le feriez avec un accompagnant.",
  },
  {
    q: "Puis-je utiliser INTEGRA FRANCE pour préparer mon examen civique ?",
    a: "Oui. La plateforme propose un parcours d'apprentissage, des quiz et des examens blancs pour vous entraîner.",
  },
  {
    q: "Les informations administratives sont-elles officielles ?",
    a: "INTEGRA FRANCE privilégie les sources officielles et affiche les sources utilisées lorsque cela est pertinent, afin que vous puissiez vérifier chaque information.",
  },
  {
    q: "INTEGRA FRANCE remplace-t-il une préfecture ou un professionnel ?",
    a: "Non. INTEGRA FRANCE ne remplace ni les administrations françaises, ni les organismes officiels, ni les professionnels du droit ou de la formation.",
  },
  {
    q: "Dois-je créer un compte ?",
    a: "Certaines informations peuvent être accessibles sans compte. La création d'un compte permet de bénéficier d'un parcours personnalisé et de conserver sa progression.",
  },
];

function FaqItem({ item, open, onToggle, index }) {
  return (
    <div className="card-integra overflow-hidden" data-testid={`faq-item-${index}`}>
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
        data-testid={`faq-accordion-trigger-${index}`}
        aria-expanded={open}
      >
        <span className="font-display text-[15px] font-bold text-slate-900 sm:text-base">{item.q}</span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.3, ease: EASE }}
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors ${open ? "bg-bleu text-white" : "bg-slate-100 text-slate-500"}`}
        >
          <Plus className="h-4 w-4" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            <p className="px-6 pb-6 text-sm leading-relaxed text-slate-500">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-20 sm:py-28">
      <div className="container-integra grid gap-12 lg:grid-cols-[1fr_1.6fr]">
        <FadeUp>
          <Overline>Questions fréquentes</Overline>
          <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Tout ce que vous devez savoir avant de commencer.
          </h2>
          <p className="mt-4 text-base text-slate-600">
            Une autre question ? Créez votre compte et posez-la directement à nos assistants IA.
          </p>
        </FadeUp>
        <div className="space-y-3.5">
          {FAQ_ITEMS.map((item, i) => (
            <FadeUp key={item.q} delay={i * 0.06}>
              <FaqItem item={item} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PartnerCTA() {
  const [showcase, setShowcase] = useState([]);

  useEffect(() => {
    api.get("/partners/showcase").then((r) => setShowcase(r.data)).catch(() => {});
  }, []);

  const cats = [
    { icon: Scale, label: "Avocat" },
    { icon: HeartHandshake, label: "Association" },
    { icon: GraduationCap, label: "Centre de formation" },
    { icon: Briefcase, label: "Recruteur" },
  ];
  return (
    <section className="pb-8 pt-4" data-testid="landing-partner-cta">
      <div className="container-integra">
        <FadeUp>
          <div className="relative overflow-hidden rounded-[2rem] border border-bleu/15 bg-white px-8 py-10 shadow-[0_24px_70px_-28px_rgba(30,63,174,0.35)] sm:px-12 sm:py-12">
            <span className="absolute left-0 top-0 h-1.5 w-full bg-gradient-to-r from-bleu via-white to-rouge" />
            <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-bleu-light blur-3xl" />
            <div className="relative flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-2xl">
                <Overline>Espace partenaires</Overline>
                <h2 className="mt-4 font-display text-2xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-3xl">
                  Vous êtes <span className="text-bleu">avocat</span>, <span className="text-bleu">association</span>,{" "}
                  <span className="text-bleu">centre de formation</span> ou <span className="text-bleu">recruteur</span> ?
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-500 sm:text-base">
                  Rejoignez l'annuaire INTEGRA FRANCE et rendez vos services visibles auprès de personnes
                  qui en ont besoin, près de chez vous. Gratuit pour les bénévoles.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {cats.map((c) => {
                    const Icon = c.icon;
                    return (
                      <span key={c.label} className="inline-flex items-center gap-1.5 rounded-full bg-bleu-light px-3.5 py-1.5 text-[11px] font-bold text-bleu">
                        <Icon className="h-3.5 w-3.5" /> {c.label}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div className="flex w-full shrink-0 flex-col items-start gap-3 lg:w-auto lg:items-center">
                <Link to="/devenir-partenaire" data-testid="landing-partner-cta-button"
                  className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-bleu to-bleu-dark px-9 py-4 text-sm font-extrabold text-white shadow-xl shadow-bleu/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-bleu/40 lg:w-auto">
                  <Handshake className="h-4.5 w-4.5" />
                  Devenir partenaire
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <p className="text-[11px] font-medium text-slate-400">Inscription en 2 minutes · Sans engagement</p>
              </div>
            </div>

            {showcase.length > 0 && (
              <div className="relative mt-8 border-t border-slate-100 pt-6" data-testid="partner-showcase">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                  Ils nous ont déjà rejoints
                </p>
                <div className="mt-3.5 flex flex-wrap gap-2.5">
                  {showcase.map((p) => (
                    <div key={p.organization}
                      className="flex items-center gap-2.5 rounded-full border border-slate-100 bg-slate-50/70 py-1.5 pl-1.5 pr-4 transition-colors hover:border-bleu/30 hover:bg-bleu-light/40"
                      data-testid="partner-showcase-item">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bleu font-display text-xs font-extrabold text-white">
                        {p.organization.charAt(0).toUpperCase()}
                      </span>
                      <span className="min-w-0">
                        <span className="block max-w-52 truncate text-xs font-bold text-slate-800">{p.organization}</span>
                        <span className="block text-[10px] text-slate-400">
                          {p.category}{p.city ? ` · ${p.city}` : ""}{p.is_nonprofit ? " · Bénévole" : ""}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </FadeUp>
      </div>
    </section>
  );
}


export function FinalCTA() {
  return (
    <section className="pb-24 pt-8">
      <div className="container-integra">
        <FadeUp>
          <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-bleu-night via-bleu-dark to-bleu px-8 py-16 text-center sm:px-16 sm:py-20">
            <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-bleu-accent/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-rouge/15 blur-3xl" />
            <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-bleu-accent via-white to-rouge" />
            <h2 className="relative font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Votre parcours commence ici.
            </h2>
            <p className="relative mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
              Comprenez mieux votre environnement, préparez votre examen et avancez dans vos démarches avec un
              accompagnement simple et personnalisé.
            </p>
            <div className="relative mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-bold text-bleu shadow-xl transition-all duration-300 hover:-translate-y-1"
                data-testid="final-cta-signup"
              >
                Commencer gratuitement <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-white/10"
                data-testid="final-cta-login"
              >
                Se connecter
              </Link>
            </div>
            <p className="relative mt-6 text-xs font-medium tracking-wide text-white/60">
              Aucun engagement. Vous gardez le contrôle de votre parcours.
            </p>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
